import React from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { type FormikProps } from 'formik';
import Loading from '@components/Shared/Loader/Loading';
import useGetOnhandInventoryItemsByInventoryId from '@hooks/INV/MasterItems/useGetOnhandInventoryItemsByInventoryId';
import useGetNonInventoryItems from '@hooks/INV/MasterItems/useGetNonInventoryItems';

interface LinesProps {
  formik: FormikProps<any>;
}

const POLines: React.FC<LinesProps> = ({ formik }) => {
  const { values } = formik;
  const { i18n, t } = useTranslation();

  const { InventoryItemsOptions, loading, error } = useGetOnhandInventoryItemsByInventoryId({
    InventoryId: values.inventoryId
  });
  const { NonInventoryItemsOptions, loading: loading2, error: error2 } = useGetNonInventoryItems();

  const calculateTotalSum = () => {
    const total = values.transactionsLinesList.reduce((sum: any, line: any) => sum + line.total, 0);
    const formattedTotal = new Intl.NumberFormat('en-US').format(total);
    return formattedTotal;
  };

  const calculateVatSum = () => {
    const vatTotal = values.transactionsLinesList.reduce((sum: any, line: any) => sum + (line.vatAmount || 0), 0);
    const formattedVatTotal = new Intl.NumberFormat('en-US').format(vatTotal);
    return formattedVatTotal;
  };

  const calculateNetTotal = () => {
    const total = values.transactionsLinesList.reduce((sum: any, line: any) => sum + line.total, 0);
    const vatTotal = values.transactionsLinesList.reduce((sum: any, line: any) => sum + (line.vatAmount || 0), 0);
    const netTotal = total + vatTotal;
    const formattedNetTotal = new Intl.NumberFormat('en-US').format(netTotal);
    return formattedNetTotal;
  };

  //----------------------------------------------------------------

  return (
    <>
      <Loading loading={loading || loading2} error={error || error2} Type='Dots'>
        <div className='  shadow-sm mb-4'>
          <div className='card-header bg-secondary bg-opacity-25 d-flex justify-content-between align-items-center'>
            <div className='d-flex align-items-center gap-3'>
              <div className='fw-bold text-end'>
                {t('الإجمالي')}:<span className='text-theme fs-5 fw-bold ms-1'> {calculateTotalSum()}</span>
              </div>
              <div className='fw-bold text-end'>
                {t('VAT')}:<span className='text-theme fs-5 fw-bold ms-1'> {calculateVatSum()}</span>
              </div>
              <div className='fw-bold text-end'>
                {t('الصافي')}:<span className='text-theme fs-5 fw-bold ms-1'> {calculateNetTotal()}</span>
              </div>
            </div>
          </div>

          <div className='card-body p-0'>
            <div className='table-responsive'>
              <div
                style={{
                  width: '120%',
                  maxHeight: '350px',
                  overflowY: 'auto',
                  overflowX: 'auto',
                  position: 'relative'
                }}
              >
                <table className='table table-hover align-middle mb-0'>
                  <thead className=' bg-theme bg-opacity-60 sticky-top shadow-lg' style={{ top: 0 }}>
                    <tr>
                      <th scope='col' className=' text-center bg-theme bg-opacity-80' style={{ width: '20px' }}>
                        #
                      </th>
                      <th scope='col' style={{ width: '90px' }}>
                        {t('نوع الصنف')}
                      </th>
                      <th scope='col' style={{ width: '350px' }}>
                        {t('الوصف')}
                      </th>
                      <th scope='col' style={{ width: '103px' }}>
                        {t('الوحدة')}
                      </th>
                      <th scope='col' style={{ width: '20px' }}>
                        {t('الكمية')}
                      </th>
                      <th scope='col' style={{ width: '20px' }}>
                        {t('السعر')}
                      </th>
                      <th scope='col' style={{ width: '30px' }}>
                        {t('نسبة الخصم')}
                      </th>
                      <th scope='col' style={{ width: '40px' }}>
                        {t('قيمة الخصم')}
                      </th>
                      <th scope='col' style={{ width: '40px' }}>
                        VAT %
                      </th>
                      <th scope='col' style={{ width: '40px' }}>
                        {t('VAT')}
                      </th>
                      <th scope='col' style={{ width: '40px' }}>
                        {t('Total')}
                      </th>
                     </tr>
                  </thead>

                  <tbody>
                    {values.transactionsLinesList.map((line: any, index: number) => (
                      <tr
                        key={index}
                        className=''
                        title={InventoryItemsOptions?.find(option => option.value === line.itemId)?.label || ''}
                      >
                        <td className='fw-semibold text-center bg-theme bg-opacity-80'>{index + 1}</td>

                        <td>
                          <select className='form-select form-select-sm' value={line.type || ''}>
                            <option value='InventoryItem'>{i18n.language === 'en' ? 'inventory' : 'مخزني'}</option>
                            <option value='ExpenseItem'>{i18n.language === 'en' ? 'Expense' : 'مصروف'}</option>
                            <option value='FixedAssetItem'>{i18n.language === 'en' ? 'Fixed Asset' : 'أصـل'}</option>
                            <option value='ServiceItem'>{i18n.language === 'en' ? 'Service' : 'خدمي'}</option>
                          </select>
                        </td>

                        <td>
                          {line.type === 'InventoryItem' ? (
                            <Select
                              classNamePrefix='react-select'
                              className='react-select-container'
                              isSearchable
                              isClearable
                              options={
                                InventoryItemsOptions?.filter(
                                  f => !values.transactionsLinesList?.some((line: any) => line.itemId === f.value)
                                )?.filter(
                                  (item, idx, self) => idx === self?.findIndex(i => i.itemId === item.itemId)
                                ) as any
                              }
                              value={
                                line.itemId === null
                                  ? null
                                  : InventoryItemsOptions?.find(option => option.value === line.itemId)
                              }
                              placeholder={i18n.language === 'ar' ? 'إختر' : 'Choose'}
                              menuPortalTarget={document.body}
                              styles={{
                                menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                                control: base => ({
                                  ...base,
                                  minHeight: '31px',
                                  height: '31px'
                                })
                              }}
                              menuPlacement='auto'
                            />
                          ) : (
                            <Select
                              classNamePrefix='react-select'
                              className='react-select-container'
                              isSearchable
                              isClearable
                              options={NonInventoryItemsOptions.filter(item => item.itemType === line.type) as any}
                              value={
                                line.itemId === null
                                  ? null
                                  : InventoryItemsOptions?.find(option => option.value === line.itemId)
                              }
                              placeholder={i18n.language === 'ar' ? 'إختر' : 'Choose'}
                              menuPortalTarget={document.body}
                              styles={{
                                menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                                control: base => ({
                                  ...base,
                                  minHeight: '31px',
                                  height: '31px'
                                })
                              }}
                            />
                          )}
                        </td>

                        <td>
                          {line.type === 'InventoryItem' ? (
                            <select
                              disabled={!line.itemId}
                              className={`form-select form-select-sm ${!line.itemId && 'bg-light'}`}
                              value={line.uom || ''}
                            >
                              <option value=''>{i18n.language === 'ar' ? 'إختر' : 'Choose'}</option>
                              {InventoryItemsOptions?.filter(f => f.itemId === line.itemId)?.map((item, idx) => (
                                <option key={idx} value={item.uom}>
                                  {item.uomName}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <select className={`form-select form-select-sm`} value={line.uom || ''}>
                              <option value=''>{i18n.language === 'ar' ? 'إختر' : 'Choose'}</option>

                              {NonInventoryItemsOptions?.filter(f => f.itemId === line.itemId)?.map((item, idx) => (
                                <option key={idx} value={item.uom}>
                                  {item.uomName}
                                </option>
                              ))}
                            </select>
                          )}
                        </td>

                        <td>
                          <input
                            type='text'
                            min='0'
                            step='1'
                            className='form-control form-control-sm'
                            value={line.qty}
                            onInput={e => {
                              const input = e.target as HTMLInputElement;
                              input.value = input.value?.replace(/[^0-9]/g, '');
                            }}
                          />
                        </td>

                        <td>
                          <div className='input-group input-group-sm'>
                            <input
                              type='text'
                              min='0'
                              step='0.01'
                              className='form-control'
                              value={line.price}
                              onInput={e => {
                                const input = e.target as HTMLInputElement;
                                input.value = input.value?.replace(/[^0-9]/g, '');
                              }}
                            />
                          </div>
                        </td>

                        <td>
                          <div className='input-group input-group-sm'>
                            <input
                              type='text'
                              min='0'
                              max='100'
                              step='0.01'
                              className='form-control'
                              value={line.discountRate}
                              onInput={e => {
                                const input = e.target as HTMLInputElement;
                                input.value = input.value?.replace(/[^0-9]/g, '');
                              }}
                            />
                          </div>
                        </td>

                        <td className='fw-semibold'>
                          {new Intl.NumberFormat('en-US', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0
                          }).format(line.discountAmount || 0)}
                        </td>

                        <td>
                          <div className='input-group input-group-sm'>
                            <input
                              type='text'
                              min='0'
                              step='0.01'
                              className='form-control'
                              value={line.vatRate}
                              onInput={e => {
                                const input = e.target as HTMLInputElement;
                                input.value = input.value?.replace(/[^0-9]/g, '');
                              }}
                            />
                          </div>
                        </td>

                        <td className='fw-semibold'>
                          {new Intl.NumberFormat('en-US', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0
                          }).format(line.vatAmount || 0)}
                        </td>

                        <td className='fw-bold text-primary'>
                          {new Intl.NumberFormat('en-US', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0
                          }).format(line.total || 0)}
                        </td>

                   
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </Loading>
    </>
  );
};

export default POLines;
